import axios from 'axios';

const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || '').trim().toLowerCase();
const AUTH_DISABLED = ['local', 'disabled', 'dev', 'none'].includes(AUTH_MODE);

// Create axios instance with base configuration
// In production, use relative URLs (same domain) or environment variable
// In development, use localhost
const getBaseURL = () => {
  const hasWindow = typeof window !== 'undefined';
  if (hasWindow && window.__DISCOVERY_API_URL__) {
    return String(window.__DISCOVERY_API_URL__).replace(/\/+$/, '');
  }
  const envValue = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.trim() : '';
  const hostname = hasWindow ? window.location.hostname : '';
  const protocol = hasWindow ? window.location.protocol : '';
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '' ||
    hostname.endsWith('.local');

  // Priority 1: Use explicit env var if provided
  if (envValue !== '') {
    const envIsAbsoluteUrl = envValue.startsWith('http://') || envValue.startsWith('https://');
    const envIsLocalUrl =
      envValue.includes('localhost') ||
      envValue.includes('127.0.0.1') ||
      envValue.includes('://0.0.0.0');

    // If the env value points to localhost but we're not on localhost, ignore it and use relative URL
    if (hasWindow && !isLocalHost && envIsLocalUrl) {
      return '';
    }

    if (envValue.startsWith('/')) {
      const normalized = envValue.replace(/\/+$/, ''); // remove trailing slashes

      // Default value '/api' (or '/') means our endpoints already include '/api'
      if (normalized === '' || normalized === '/api') {
        return '';
      }

      // Allow custom prefixes (e.g., '/backend')
      return normalized.startsWith('/') ? normalized : `/${normalized}`;
    }

    if (envIsAbsoluteUrl) {
      return envValue;
    }

    // Treat as relative path
    return `/${envValue.replace(/^\/+/, '')}`;
  }

  // Priority 2: Runtime detection - for production domains use relative URL
  if (hasWindow && !isLocalHost) {
    return '';
  }

  // Priority 3: If running on localhost but via HTTPS (dev over secure tunnel), still use relative path
  if (hasWindow && isLocalHost && protocol === 'https:') {
    return '';
  }

  // Priority 4: NODE_ENV hints production
  if (process.env.NODE_ENV === 'production') {
    return '';
  }

  // Development fallback - explicit localhost for local dev scenarios
  return 'http://localhost:50008';
};

// Get base URL and log for debugging (remove in production if needed)
const baseURL = getBaseURL();
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', baseURL, 'REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
}

const api = axios.create({
  baseURL: baseURL,
  timeout: 10000,
  withCredentials: true, // Important for session cookies
});

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Request interceptor to add auth headers if needed
api.interceptors.request.use(
  (config) => {
    // Add any auth headers here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let _redirectingToLogin = false;
function _isEmbedded() {
  if (typeof window === 'undefined') return false;
  if (window.__DISCOVERY_EMBEDDED__) return true;
  try {
    return window.self !== window.top;
  } catch (_) {
    return true;
  }
}
function _resolveIdpLoginUrl() {
  if (AUTH_DISABLED) return null;
  if (typeof window === 'undefined') return null;
  const here = window.location.href;
  const runtime = window.__DISCOVERY_IDP_URL__;
  const envVar = typeof process !== 'undefined' && process.env && process.env.REACT_APP_IDP_URL;
  const isLocal =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const idpBase = (runtime || envVar || (isLocal ? 'http://localhost:50005' : '')).replace(/\/+$/, '');
  if (!idpBase) return null;
  return `${idpBase}/login/?next=${encodeURIComponent(here)}`;
}
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !AUTH_DISABLED &&
      typeof window !== 'undefined' &&
      !_redirectingToLogin &&
      !_isEmbedded()
    ) {
      _redirectingToLogin = true;
      const target = _resolveIdpLoginUrl();
      if (target) {
        window.location.replace(target);
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API - simplified (no IDP)
export const authAPI = {
  check: () => api.get('/auth/check'),
  login: () => {
    // No login needed - just redirect to home
    window.location.href = '/';
  },
  logout: async () => {
    // No logout needed - just redirect to home
    window.location.href = '/';
  },
  status: () => api.get('/auth/status'),
};

// ============================================================================
// COMPANY LOOKUP API (canonical backend contract)
// ============================================================================

const INTERFACE_BY_ENDPOINT_KEY = {
  'AAS Discovery': 'AAS-DISCOVERY-3.1',
  'AAS Registry': 'AAS-REGISTRY-3.1',
  'AAS Repository': 'AAS-REPOSITORY-3.1',
  'Submodel Registry': 'SUBMODEL-REGISTRY-3.1',
  'Submodel Repository': 'SUBMODEL-REPOSITORY-3.1',
  'Concept Description Repository': 'AASX-FILE-3.1',
  'MQTT Broker': 'MQTT-BROKER-3.1.1',
  'DPP Service': 'DPP-SERVICE-1.0'
};

const INTERFACE_LABELS = [
  ['AAS-DISCOVERY', 'AAS Discovery'],
  ['AAS-REGISTRY', 'AAS Registry'],
  ['AAS-REPOSITORY', 'AAS Repository'],
  ['SUBMODEL-REGISTRY', 'Submodel Registry'],
  ['SUBMODEL-REPOSITORY', 'Submodel Repository'],
  ['AASX-FILE', 'AASX File'],
  ['MQTT-BROKER', 'MQTT Broker'],
  ['DPP-SERVICE', 'DPP Service']
];

const DEFAULT_PER_PAGE = 6;

const normalizeDomain = (domain) =>
  String(domain || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');

const dedupeValues = (values = []) => {
  const seen = new Set();
  return values.filter((value) => {
    const text = String(value || '').trim();
    if (!text) return false;
    const key = text.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const compactStringMap = (value) => {
  if (!isPlainObject(value)) return {};
  return Object.entries(value).reduce((acc, [key, raw]) => {
    const text = String(raw || '').trim();
    if (text) acc[key] = text;
    return acc;
  }, {});
};

const inferProtocolInfo = (url) => {
  const href = String(url || '').trim();
  const lower = href.toLowerCase();
  if (lower.startsWith('https://')) {
    return { href, endpointProtocol: 'HTTPS', endpointProtocolVersion: '1.1' };
  }
  if (lower.startsWith('http://')) {
    return { href, endpointProtocol: 'HTTP', endpointProtocolVersion: '1.1' };
  }
  if (lower.startsWith('wss://')) {
    return { href, endpointProtocol: 'WSS' };
  }
  if (lower.startsWith('ws://')) {
    return { href, endpointProtocol: 'WS' };
  }
  return { href };
};

const endpointInterfaceToLabel = (interfaceName) => {
  const normalized = String(interfaceName || '').toUpperCase();
  const match = INTERFACE_LABELS.find(([prefix]) => normalized.startsWith(prefix));
  return match ? match[1] : interfaceName || 'Endpoint';
};

const buildRegexPatterns = (assetPattern = {}) => {
  const patterns = [];
  const globalAssetIds = Array.isArray(assetPattern.global_asset_ids) ? assetPattern.global_asset_ids : [];
  globalAssetIds.forEach((id) => {
    const value = String(id || '').trim();
    if (value) patterns.push(`^${value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
  });
  if (assetPattern.global_asset_id) {
    patterns.push(`^${String(assetPattern.global_asset_id).trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
  }
  if (assetPattern.asset_id_starts_with) {
    patterns.push(`^${String(assetPattern.asset_id_starts_with).trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
  }
  if (assetPattern.asset_id_contains) {
    patterns.push(String(assetPattern.asset_id_contains).trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  }
  if (assetPattern.asset_id_ends_with) {
    patterns.push(`${String(assetPattern.asset_id_ends_with).trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
  }
  return dedupeValues(patterns);
};

const buildCompanyPayload = (legacyFormData = {}) => {
  const domain = normalizeDomain(legacyFormData.domain);
  const companyName = String(legacyFormData.company_data?.company_name || '').trim();
  const infrastructureEndpoints = legacyFormData.infrastructure_endpoints || {};

  const endpoints = Object.entries(infrastructureEndpoints)
    .filter(([key, value]) => INTERFACE_BY_ENDPOINT_KEY[key] && String(value || '').trim() !== '')
    .map(([key, value]) => ({
      protocolInformation: inferProtocolInfo(value),
      interface: INTERFACE_BY_ENDPOINT_KEY[key]
    }));

  const assetRegexPatterns = buildRegexPatterns(legacyFormData.asset_id_pattern || {});

  return {
    name: companyName,
    domain,
    endpoints,
    nameOptions: dedupeValues([companyName]),
    assetIdRegexPatterns: assetRegexPatterns,
    idLinkRegexPatterns: assetRegexPatterns,
    company_data: isPlainObject(legacyFormData.company_data)
      ? {
          ...legacyFormData.company_data,
          company_name: legacyFormData.company_data.company_name || companyName,
          homepage_url: legacyFormData.company_data.homepage_url || (domain ? `https://${domain}` : '')
        }
      : {
          company_name: companyName,
          homepage_url: domain ? `https://${domain}` : ''
        },
    capabilities: isPlainObject(legacyFormData.capabilities) ? legacyFormData.capabilities : {},
    infrastructure_endpoints: isPlainObject(legacyFormData.infrastructure_endpoints)
      ? legacyFormData.infrastructure_endpoints
      : {},
    asset_id_pattern: isPlainObject(legacyFormData.asset_id_pattern) ? legacyFormData.asset_id_pattern : {},
    authentication_type:
      typeof legacyFormData.authentication_type === 'string' ? legacyFormData.authentication_type : ''
  };
};

const mapCompanyToLegacyDescriptor = (company = {}) => {
  const backendCompanyData = isPlainObject(company.company_data) ? company.company_data : {};
  const name = String(company.name || backendCompanyData.company_name || '').trim();
  const domain = normalizeDomain(company.domain);
  const endpoints = Array.isArray(company.endpoints) ? company.endpoints : [];
  const inferredInfrastructureEndpoints = {};

  endpoints.forEach((endpoint) => {
    const href =
      endpoint?.protocolInformation?.href ||
      endpoint?.url ||
      '';
    const iface = endpoint?.interface || endpoint?.type || '';
    const label = endpointInterfaceToLabel(iface);
    if (href && label && !inferredInfrastructureEndpoints[label]) {
      inferredInfrastructureEndpoints[label] = href;
    }
  });

  const regexPatterns = Array.isArray(company.assetIdRegexPatterns) ? dedupeValues(company.assetIdRegexPatterns) : [];
  const backendInfrastructure = compactStringMap(company.infrastructure_endpoints);
  const backendAssetPattern = isPlainObject(company.asset_id_pattern) ? company.asset_id_pattern : {};
  const inferredAssetPattern = {
    global_asset_id: regexPatterns[0] || '',
    global_asset_ids: regexPatterns,
    asset_id_starts_with: '',
    asset_id_contains: '',
    asset_id_ends_with: '',
    serial_number_pattern: {
      serial_number_starts_with: '',
      serial_number_ends_with: '',
      serial_number_contains: '',
      serial_number_format: ''
    }
  };
  const mergedAssetPattern = {
    ...inferredAssetPattern,
    ...backendAssetPattern,
    serial_number_pattern: {
      ...inferredAssetPattern.serial_number_pattern,
      ...(isPlainObject(backendAssetPattern.serial_number_pattern) ? backendAssetPattern.serial_number_pattern : {})
    }
  };
  const mergedCompanyData = {
    company_name: name || domain,
    company_description: '',
    homepage_url: domain ? `https://${domain}` : '',
    industries: [],
    main_product_groups: [],
    ...backendCompanyData
  };
  if (!mergedCompanyData.company_name) {
    mergedCompanyData.company_name = name || domain;
  }
  if (!mergedCompanyData.homepage_url && domain) {
    mergedCompanyData.homepage_url = `https://${domain}`;
  }

  return {
    domain,
    company_data: mergedCompanyData,
    infrastructure_endpoints: { ...inferredInfrastructureEndpoints, ...backendInfrastructure },
    capabilities: isPlainObject(company.capabilities) ? company.capabilities : {},
    asset_id_pattern: mergedAssetPattern,
    authentication_type:
      typeof company.authentication_type === 'string' ? company.authentication_type : '',
    __canonical: company
  };
};

const detectLookupParamsFromSearch = (query) => {
  const trimmed = String(query || '').trim();
  if (!trimmed) return {};
  if (/^https?:\/\//i.test(trimmed) || /\/asset\/|\/ids\/asset\//i.test(trimmed)) {
    return { asset_id: trimmed };
  }
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(trimmed)) {
    return { domain: trimmed };
  }
  return { name: trimmed };
};

export const companyLookupAPI = {
  lookup: ({ name, domain, asset_id, id_link } = {}) => {
    const params = {};
    if (name) params.name = name;
    if (domain) params.domain = domain;
    if (asset_id || id_link) params.asset_id = asset_id || id_link;
    return api.get('/api/v2/companies', { params });
  },
  getAll: () => api.get('/api/v2/companies'),
  getDescriptors: (params = {}) => api.get('/api/v2/companies', { params }),
  add: (descriptor) => api.post('/api/v2/companies', { data: descriptor }),
};

export const dataProvidersAPI = {
  /**
   * Query endpoint for all data provider searches
   * @param {Object} params - Query parameters
   * @param {string} params.domain - Filter by domain
   * @param {string} params.company_name - Filter by company name
   * @param {string} params.asset_id - Find by asset ID (smart matching)
   * @param {string} params.serial_number - Find by serial number (smart matching)
   * @param {string} params.aas_id - Find by AAS ID
   * @param {string} params.capability - Filter by capability
   * @param {string} params.submodel - Filter by submodel
   * @param {string} params.product_group - Filter by product group
   * @param {string} params.keyword - Filter by keyword
   * @param {string} params.industry - Filter by industry
   * @param {string} params.search - Universal search
   * @param {number} params.page - Page number
   * @param {number} params.per_page - Items per page
   * @returns {Promise} Data providers matching the criteria
   */
  query: async (params = {}) => {
    const page = Math.max(1, Number(params.page) || 1);
    const perPage = Math.max(1, Number(params.per_page) || DEFAULT_PER_PAGE);

    const lookupParams = {};
    let clientFilterText = '';
    let requiresClientFiltering = false;

    if (params.domain) {
      lookupParams.domain = params.domain;
    } else if (params.asset_id || params.id_link) {
      lookupParams.asset_id = params.asset_id || params.id_link;
    } else if (params.company_name || params.name) {
      lookupParams.name = params.company_name || params.name;
    } else if (params.search) {
      const detected = detectLookupParamsFromSearch(params.search);
      if (detected.asset_id || detected.domain) {
        Object.assign(lookupParams, detected);
      } else {
        requiresClientFiltering = true;
        clientFilterText = String(params.search || '').trim();
      }
    } else {
      const fallbackFilters = [
        params.serial_number,
        params.aas_id,
        params.capability,
        params.submodel,
        params.product_group,
        params.keyword,
        params.industry
      ].filter(Boolean);
      if (fallbackFilters.length > 0) {
        requiresClientFiltering = true;
        clientFilterText = String(fallbackFilters[0] || '').trim();
      }
    }

    const response = await api.get('/api/v2/companies', {
      params: requiresClientFiltering ? {} : lookupParams
    });
    const companies = Array.isArray(response.data?.data) ? response.data.data : [];
    let descriptors = companies.map(mapCompanyToLegacyDescriptor);

    if (clientFilterText) {
      const needle = clientFilterText.toLowerCase();
      descriptors = descriptors.filter((descriptor) =>
        JSON.stringify(descriptor).toLowerCase().includes(needle)
      );
    }

    const start = (page - 1) * perPage;
    const paged = descriptors.slice(start, start + perPage);
    const total = descriptors.length;
    const totalPages = Math.max(1, Math.ceil(total / perPage));

    return {
      ...response,
      data: {
        success: true,
        data: paged,
        pagination: {
          page,
          per_page: perPage,
          total,
          total_pages: totalPages
        },
        meta: {
          query_type: requiresClientFiltering
            ? 'client_filter_search'
            : (Object.keys(lookupParams).length > 0 ? 'companies_lookup' : 'list_all'),
          total_providers: total
        }
      }
    };
  },
  
  /**
   * Get all infrastructure endpoints across providers
   * @param {Object} params - Query parameters
   * @param {string} params.company_name - Filter by company name
   * @param {string} params.endpoint_type - Filter by endpoint type
   * @returns {Promise} Infrastructure endpoints
   */
  getInfrastructureEndpoints: async (params = {}) => {
    const response = await api.get('/api/v2/companies');
    const companies = Array.isArray(response.data?.data) ? response.data.data : [];
    const companyFilter = String(params.company_name || '').toLowerCase();
    const endpointTypeFilter = String(params.endpoint_type || '').toLowerCase();

    const result = [];
    let totalEndpoints = 0;
    const endpointTypes = new Set();

    companies.forEach((company) => {
      const providerName = String(company.name || '').trim();
      const providerDomain = normalizeDomain(company.domain);
      if (companyFilter && !providerName.toLowerCase().includes(companyFilter)) return;

      const allEndpoints = {};
      (Array.isArray(company.endpoints) ? company.endpoints : []).forEach((endpoint) => {
        const label = endpointInterfaceToLabel(endpoint?.interface || endpoint?.type || '');
        const href = endpoint?.protocolInformation?.href || endpoint?.url || '';
        if (!href || !label) return;
        if (endpointTypeFilter && !label.toLowerCase().includes(endpointTypeFilter)) return;
        allEndpoints[label] = href;
      });

      const names = Object.keys(allEndpoints);
      if (names.length === 0) return;

      names.forEach((name) => endpointTypes.add(name));
      totalEndpoints += names.length;
      result.push({
        provider_domain: providerDomain,
        provider_name: providerName,
        endpoints: allEndpoints,
        endpoint_count: names.length
      });
    });

    return {
      ...response,
      data: {
        success: true,
        data: result,
        summary: {
          total_providers: result.length,
          total_endpoints: totalEndpoints,
          endpoint_types: Array.from(endpointTypes).sort()
        }
      }
    };
  }
};

// Registry API - Legacy for backward compatibility
export const registryAPI = {
  // Use smart search that automatically detects asset IDs, serial numbers, etc.
  getDescriptors: (params = {}) => {
    // If search query provided, use smart search to detect type (asset ID, serial number, etc.)
    if (params.search) {
      // Extract pagination params
      const paginationParams = {
        page: params.page,
        per_page: params.per_page
      };
      // Use smart search which will detect asset IDs and route correctly
      return smartSearch(params.search, paginationParams);
    }
    // Otherwise use direct query
    return dataProvidersAPI.query(params);
  },
  
  // Legacy endpoints
  getDescriptor: (id) => api.get(`/api/registry-descriptors/${id}`),
  getRegistryAccess: (id) => api.get(`/api/registry-access/${id}`),
  proxyRegistry: (id) => api.get(`/api/proxy-registry/${id}`),
  getLocalContent: (id) => api.get(`/api/local-registry-content/${id}`),
  forwardRequest: (url) => api.get('/api/forward-request', { params: { url } }),
  
  // ============================================================================
  // NEW SPEC API ENDPOINTS (DQL & Canonical Data Shapes)
  // ============================================================================
  
  // Yellow Pages - Company discovery
  /**
   * Query companies using DQL (Discovery Query Language)
   * @param {string} dqlQuery - DQL query string, e.g., "FROM Company WHERE legalName ^= \"ACME\" OR \"motion\" in tags"
   * @returns {Promise} Response with companies matching the query
   */
  queryCompanies: (dqlQuery) => api.get('/api/v2/yellow-pages/companies/query', { 
    params: { q: dqlQuery } 
  }),
  
  /**
   * Get company by companyId, assetId, or domain
   * @param {string} companyId - Company ID (urn:uuid:...), assetId (urn:aas:company:...), or domain
   * @returns {Promise} Company details
   */
  getCompany: (companyId) => api.get(`/api/v2/yellow-pages/companies/${companyId}`),
  
  // Yellow Pages - Capability discovery
  /**
   * Query capabilities using DQL
   * @param {string} dqlQuery - DQL query string, e.g., "FROM Capability WHERE category = \"motion\" AND \"pmsm\" in tags"
   * @returns {Promise} Response with capabilities matching the query
   */
  queryCapabilities: (dqlQuery) => api.get('/api/v2/yellow-pages/capabilities/query', { 
    params: { q: dqlQuery } 
  }),
  
  // Address Book - Holder Inventory
  /**
   * Get HolderInventory by holderCompanyId
   * @param {string} holderId - Holder company ID (urn:uuid:...)
   * @returns {Promise} HolderInventory with all asset pointers
   */
  getHolderInventory: (holderId) => api.get(`/api/v2/address-book/holder-inventory/${holderId}`),
  
  /**
   * Query assets in HolderInventory using DQL
   * @param {string} holderId - Holder company ID
   * @param {string} dqlQuery - DQL query string, e.g., "FROM HolderInventory WHERE assetId = \"urn:iec:61406:acme:PN12345:SN98765\""
   * @returns {Promise} Response with matching asset pointers
   */
  queryHolderInventory: (holderId, dqlQuery) => api.get(`/api/v2/address-book/holder-inventory/${holderId}/query`, { 
    params: { q: dqlQuery } 
  }),
  
  // Address Book - Asset discovery
  /**
   * Find all holders that have a specific asset in their inventory
   * @param {string} assetId - Asset ID (e.g., urn:iec:61406:acme:PN12345:SN98765)
   * @returns {Promise} List of holders with this asset
   */
  getAssetHolders: (assetId) => api.get(`/api/v2/address-book/assets/${assetId}/holders`),
  
  /**
   * Get AccessLink for an asset (credential-free pointer bundle)
   * @param {string} assetId - Asset ID
   * @returns {Promise} AccessLink with links to AAS_REGISTRY, AAS_SERVER, HOLDER_INVENTORY
   */
  getAccessLink: (assetId) => api.get(`/api/v2/address-book/access-links/${assetId}`),
  
  // ============================================================================
  // LEGACY ENDPOINTS (deprecated - use DQL endpoints above)
  // ============================================================================
  searchAssets: (filters = {}) => api.get('/api/v2/address-book/assets', { params: filters }),
  getAsset: (assetId) => api.get(`/api/v2/address-book/assets/${assetId}`),
  getEndpoints: (domain) => api.get(`/api/v2/address-book/endpoints/${domain}`),
  searchCompanies: (filters = {}) => api.get('/api/v2/yellow-pages/companies', { params: filters }),
  
  // Simplified registry call - no JWT tokens needed
  callRegistryDirect: async (registryUrl, path, token, method = 'GET', data = null) => {
    const config = {
      method,
      url: `${registryUrl}${path}`,
      headers: {
        'Content-Type': 'application/json'
      }
    };
    
    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      config.data = data;
    }
    
    return axios(config);
  }
};

// AASX API
export const aasxAPI = {
  getDescriptors: () => api.get('/api/aasx/descriptors'),
  getDescriptor: (id) => api.get(`/api/aasx/descriptors/${id}`),
  getServers: (id) => api.get(`/api/aasx/descriptors/${id}/servers`),
  getCategorization: (id) => api.get(`/api/aasx/descriptors/${id}/categorization`),
};

// Onboarding API
export const onboardAPI = {
  submitOrganization: (legacyFormData) => {
    const payload = buildCompanyPayload(legacyFormData);
    return api.post('/api/v2/companies', { data: payload });
  },
};

// ============================================================================
// SMART SEARCH - Automatically routes to correct data provider query
// ============================================================================

export const smartSearch = (query, paginationParams = {}) => {
  /**
   * Smart search that automatically detects search type and routes to unified endpoint
   * @param {string} query - Search query
   * @param {Object} paginationParams - Pagination parameters (page, per_page)
   * @returns {Promise} Data providers matching the query
   */
  
  const params = { ...paginationParams };
  
  if (!query || !query.trim()) {
    // Empty query returns all providers
    return dataProvidersAPI.query(params);
  }
  
  const trimmedQuery = query.trim();
  
  // Check if it's a URL or contains URL patterns (Asset ID)
  // Enhanced detection for asset ID URLs like:
  // - https://admin-shell.io/asset/123
  // - https://admin-shell.io/idta/asset/123
  // - http://example.com/asset/123
  const isUrlPattern = /^https?:\/\//i.test(trimmedQuery);
  const hasUrlStructure = trimmedQuery.includes('://') && 
    (trimmedQuery.match(/\.(com|io|org|net|de)(\/|$)/i) || 
     trimmedQuery.includes('/asset/') || 
     trimmedQuery.includes('/idta/') ||
     trimmedQuery.includes('/ids/asset/'));
  
  if (isUrlPattern || hasUrlStructure) {
    // This is definitely an asset ID search
    if (process.env.NODE_ENV === 'development') {
      console.log('Detected asset ID URL:', trimmedQuery);
    }
    return dataProvidersAPI.query({ ...params, asset_id: trimmedQuery });
  }
  
  // Check if query contains asset ID keywords (admin shell io, asset, ids/asset, etc.)
  // This handles searches like "admin shell io asset" or "phoenixcontact asset"
  const queryLower = trimmedQuery.toLowerCase();
  const assetIdKeywords = ['asset', 'ids/asset', '/asset/', 'asset/', 'asset id', 'assetid'];
  const hasAssetKeyword = assetIdKeywords.some(keyword => queryLower.includes(keyword));
  
  // Check for domain patterns (with or without spaces/hyphens)
  // "admin shell io" -> "admin-shell.io" or "admin.shell.io"
  // Also check for partial matches like "admin shell" or "shell io"
  const domainPatterns = [
    /admin[\s\-_]*shell[\s\-_]*io/i,     // "admin shell io", "admin-shell-io", "admin_shell_io"
    /admin[\s\-_]*shell/i,                // "admin shell" (partial)
    /shell[\s\-_]*io/i,                   // "shell io" (partial)
    /phoenix[\s\-_]*contact/i,            // "phoenix contact", "phoenix-contact"
    /lenze/i,                             // "lenze"
    /siemens/i,                           // "siemens"
    /[a-z0-9-]+\.(io|com|org|net|de)(\/|$)/i   // Any domain pattern like "example.com" or "example.com/asset"
  ];
  const hasDomainPattern = domainPatterns.some(pattern => pattern.test(trimmedQuery));
  
  // Also check if query contains domain-like words (admin, shell, io together)
  const hasDomainKeywords = (queryLower.includes('admin') && queryLower.includes('shell') && queryLower.includes('io')) ||
                           (queryLower.includes('phoenix') && queryLower.includes('contact')) ||
                           queryLower.includes('lenze') ||
                           queryLower.includes('siemens');
  
  // Debug logging (can be removed in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('Smart Search Detection:', {
      query: trimmedQuery,
      hasAssetKeyword,
      hasDomainPattern,
      hasDomainKeywords,
      queryLower
    });
  }
  
  // If query contains "asset" keyword AND domain/company name, it's likely an asset ID search
  // OR if it contains domain pattern with TLD indicators
  if (hasAssetKeyword && (hasDomainPattern || hasDomainKeywords || queryLower.match(/\b(io|com|org|net|de)\b/i))) {
    // This looks like an asset ID pattern search - use asset_id parameter
    // The backend will match against asset_id_pattern fields
    if (process.env.NODE_ENV === 'development') {
      console.log('Routing to asset_id search:', trimmedQuery);
    }
    return dataProvidersAPI.query({ ...params, asset_id: trimmedQuery });
  }
  
  // Also check if it's just a domain pattern (even without "asset" keyword)
  // This handles queries like "admin shell io" that should match asset ID patterns
  if ((hasDomainPattern || hasDomainKeywords) && 
      (queryLower.match(/\b(io|com|org|net|de)\b/i) || queryLower.match(/admin[\s-]*shell/i))) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Routing to asset_id search (domain pattern):', trimmedQuery);
    }
    return dataProvidersAPI.query({ ...params, asset_id: trimmedQuery });
  }
  
  // Check if it looks like a serial number (contains common patterns like LEN-, PHX-, IDTA-)
  if (trimmedQuery.match(/^[A-Z]{2,5}[-_]/i) || 
      trimmedQuery.match(/^(SN|SERIAL|S\/N)[-_]?/i) || 
      trimmedQuery.match(/[-_][0-9]{4,}/)) {
    return dataProvidersAPI.query({ ...params, serial_number: trimmedQuery });
  }
  
  // Check if it looks like a specific domain
  if (trimmedQuery.match(/^[a-z0-9-]+\.(com|io|org|net|de)$/i)) {
    return dataProvidersAPI.query({ ...params, domain: trimmedQuery });
  }
  
  // Check if it looks like a submodel name (PascalCase)
  if (trimmedQuery.match(/^[A-Z][a-z]+([A-Z][a-z]+)+$/) && 
      (trimmedQuery.includes('Nameplate') || trimmedQuery.includes('Data') || 
       trimmedQuery.includes('Documentation') || trimmedQuery.includes('Information'))) {
    return dataProvidersAPI.query({ ...params, submodel: trimmedQuery });
  }
  
  // Otherwise, use universal search (searches all fields)
  return dataProvidersAPI.query({ ...params, search: trimmedQuery });
};

// Legacy mapping API - redirects to unified endpoint
export const mappingAPI = {
  byAssetId: (assetId) => dataProvidersAPI.query({ asset_id: assetId }),
  bySerialNumber: (serialNumber) => dataProvidersAPI.query({ serial_number: serialNumber }),
  byAasId: (aasId) => dataProvidersAPI.query({ aas_id: aasId }),
  byCompanyName: (companyName) => dataProvidersAPI.query({ company_name: companyName }),
  bySubmodel: (submodel) => dataProvidersAPI.query({ submodel }),
  byCapability: (capability) => dataProvidersAPI.query({ capability }),
  byProductGroup: (productGroup) => dataProvidersAPI.query({ product_group: productGroup }),
  smartSearch: smartSearch
};

// System API
export const systemAPI = {
  performance: () => api.get('/api/performance'),
};

export default api;
